class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this._openFrame = null;
    this._openFrame2 = null;
    this._openFocusTimer = null;
    this._openTransitionHandler = null;
    this._openInner = null;
    this._closeTimer = null;
    this._closeTransitionHandler = null;
    this._closeInner = null;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  _prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  _cancelOpenFrames() {
    if (this._openFrame) cancelAnimationFrame(this._openFrame);
    if (this._openFrame2) cancelAnimationFrame(this._openFrame2);
    this._openFrame = null;
    this._openFrame2 = null;
  }

  _clearOpenTransition() {
    if (this._openInner && this._openTransitionHandler) {
      this._openInner.removeEventListener('transitionend', this._openTransitionHandler);
    }
    if (this._openFocusTimer) clearTimeout(this._openFocusTimer);
    this._openInner = null;
    this._openTransitionHandler = null;
    this._openFocusTimer = null;
  }

  _clearCloseTransition() {
    if (this._closeInner && this._closeTransitionHandler) {
      this._closeInner.removeEventListener('transitionend', this._closeTransitionHandler);
    }
    if (this._closeTimer) clearTimeout(this._closeTimer);
    this._closeInner = null;
    this._closeTransitionHandler = null;
    this._closeTimer = null;
  }

  _trapDrawerFocus() {
    if (!this.classList.contains('active')) return;

    const containerToTrapFocusOn = this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty')
      : document.getElementById('CartDrawer');
    const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');

    if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
  }

  _finishClose() {
    if (this.classList.contains('active')) return;
    this._clearCloseTransition();
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    this._cancelOpenFrames();
    this._clearOpenTransition();
    this._clearCloseTransition();
    document.body.classList.add('overflow-hidden');

    const inner = this.querySelector('.drawer__inner');

    if (this._prefersReducedMotion()) {
      this.classList.add('animate', 'active');
      this._openFrame = requestAnimationFrame(() => {
        this._openFrame = null;
        this._trapDrawerFocus();
      });
      return;
    }

    if (inner) {
      this._openInner = inner;
      this._openTransitionHandler = (event) => {
        if (event.target !== inner || event.propertyName !== 'transform') return;
        this._clearOpenTransition();
        this._trapDrawerFocus();
      };
      inner.addEventListener('transitionend', this._openTransitionHandler);

      this._openFocusTimer = setTimeout(() => {
        this._clearOpenTransition();
        this._trapDrawerFocus();
      }, 480);
    }

    // Two frames guarantee a fully committed off-screen state before the compositor starts the slide.
    this._openFrame = requestAnimationFrame(() => {
      this._openFrame = null;
      this._openFrame2 = requestAnimationFrame(() => {
        this._openFrame2 = null;
        this.classList.add('animate', 'active');
      });
    });
  }

  close() {
    this._cancelOpenFrames();
    this._clearOpenTransition();

    const wasActive = this.classList.contains('active');
    const inner = this.querySelector('.drawer__inner');
    this.classList.remove('active');

    if (!wasActive || this._prefersReducedMotion() || !inner) {
      this._finishClose();
      return;
    }

    this._clearCloseTransition();
    this._closeInner = inner;
    this._closeTransitionHandler = (event) => {
      if (event.target !== inner || event.propertyName !== 'transform') return;
      this._finishClose();
    };
    inner.addEventListener('transitionend', this._closeTransitionHandler);

    // Keep the page locked until the drawer has actually left the screen.
    this._closeTimer = setTimeout(() => this._finishClose(), 480);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    requestAnimationFrame(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
